import React, { useCallback, useEffect, useMemo, useState } from "react";
import NavBar from "../components/NavBar";
import GenderBadge from "../components/GenderBadge";
import { apiFetch } from "../api";
import { useLoading } from "../contexts/LoadingContext";
import {
  RefreshIcon,
  UsersIcon,
  SaveIcon,
  CancelIcon,
} from "../components/icons";
import { formatDate } from "../utils/dateUtils";

const STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "ended", label: "Ended" },
  { value: "rejected", label: "Rejected" },
  { value: "all", label: "All" },
];

const pillClass = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-800",
  ended: "bg-gray-100 text-gray-700",
  rejected: "bg-red-100 text-red-800",
};

// Tiny interactive star widget. `value` is the current rating, onChange
// fires with the picked number. Pass readOnly to use as a display-only.
const StarRating = ({ value, onChange, size = "h-4 w-4", readOnly = false }) => {
  const safe = Math.max(0, Math.min(5, Math.round(value || 0)));
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          disabled={readOnly}
          onClick={() => !readOnly && onChange?.(i === safe ? 0 : i)}
          aria-label={`${i} star${i === 1 ? "" : "s"}`}
          className={readOnly ? "cursor-default" : "cursor-pointer"}
        >
          <svg
            viewBox="0 0 24 24"
            className={`${size} ${
              i <= safe
                ? "text-yellow-500 fill-yellow-500"
                : "text-[var(--color-text-muted)] fill-none"
            }`}
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
          >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        </button>
      ))}
    </span>
  );
};

const BureauAssociations = () => {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [responseDrafts, setResponseDrafts] = useState({});
  // Map<requestId, { rating, comment }> — local draft state for the
  // client-review widget on approved/ended rows. Initialised lazily.
  const [reviewDrafts, setReviewDrafts] = useState({});
  // requestId of the row currently being "ended by bureau" — drives the
  // small confirm panel (final note + optional rating + review).
  const [endingId, setEndingId] = useState(null);
  const [busyIds, setBusyIds] = useState(new Set());
  const { setLoading: setGlobalLoading, setGlobalLoadingMessage } = useLoading();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setGlobalLoadingMessage("Loading association requests…");
      setGlobalLoading(true);
      const res = await apiFetch("/bureau-associations");
      setItems(res?.data || []);
      apiFetch("/bureau-associations/mark-seen", { method: "POST" }).catch(
        () => {}
      );
      window.dispatchEvent(new CustomEvent("associations:seen"));
    } catch (err) {
      setError(err.message || "Failed to load");
    } finally {
      setLoading(false);
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  }, [setGlobalLoading, setGlobalLoadingMessage]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const c = {
      pending: 0,
      approved: 0,
      ended: 0,
      rejected: 0,
      all: items.length,
    };
    items.forEach((it) => {
      if (c[it.status] !== undefined) c[it.status] += 1;
    });
    return c;
  }, [items]);

  const filtered =
    statusFilter === "all"
      ? items
      : items.filter((it) => it.status === statusFilter);

  const decide = async (id, action) => {
    setBusyIds((s) => new Set(s).add(id));
    try {
      await apiFetch(`/bureau-associations/${id}/${action}`, {
        method: "PATCH",
        body: JSON.stringify({
          bureauResponse: responseDrafts[id] || undefined,
        }),
      });
      await load();
      setResponseDrafts((d) => {
        const next = { ...d };
        delete next[id];
        return next;
      });
    } catch (err) {
      setError(err.message || `Failed to ${action}`);
    } finally {
      setBusyIds((s) => {
        const next = new Set(s);
        next.delete(id);
        return next;
      });
    }
  };

  // Initialise the review-draft slot from the server row, then update.
  const setDraftField = (id, field, value, item) => {
    setReviewDrafts((d) => {
      const existing = d[id] || {
        rating: item?.clientRating || 0,
        comment: item?.clientReview || "",
      };
      return { ...d, [id]: { ...existing, [field]: value } };
    });
  };

  const draftFor = (id, item) =>
    reviewDrafts[id] || {
      rating: item?.clientRating || 0,
      comment: item?.clientReview || "",
    };

  // End an approved association by bureau action. Pulls the final-note
  // textarea AND any draft rating/review on the same row so everything is
  // closed out in a single API call.
  const endAssociation = async (item) => {
    if (
      !window.confirm(
        `End your association with ${item.publicUser?.fullName || "this client"}? They'll be notified.`
      )
    )
      return;
    const id = item._id;
    const draft = draftFor(id, item);
    setBusyIds((s) => new Set(s).add(id));
    try {
      await apiFetch(`/bureau-associations/${id}/end`, {
        method: "PATCH",
        body: JSON.stringify({
          bureauResponse: responseDrafts[id] || undefined,
          clientRating: draft.rating || undefined,
          clientReview: draft.comment || undefined,
        }),
      });
      setEndingId(null);
      await load();
    } catch (err) {
      setError(err.message || "Failed to end association");
    } finally {
      setBusyIds((s) => {
        const next = new Set(s);
        next.delete(id);
        return next;
      });
    }
  };

  // Save just the rating + review without changing the association
  // status. Works for approved (active) or ended (post-relationship)
  // entries — the bureau can revise later.
  const saveClientReview = async (item) => {
    const id = item._id;
    const draft = draftFor(id, item);
    if (!draft.rating && !(draft.comment || "").trim()) {
      setError("Pick a rating or write something before saving.");
      return;
    }
    setBusyIds((s) => new Set(s).add(id));
    try {
      await apiFetch(`/bureau-associations/${id}/client-review`, {
        method: "PATCH",
        body: JSON.stringify({
          clientRating: draft.rating || undefined,
          clientReview: draft.comment || undefined,
        }),
      });
      await load();
    } catch (err) {
      setError(err.message || "Failed to save review");
    } finally {
      setBusyIds((s) => {
        const next = new Set(s);
        next.delete(id);
        return next;
      });
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <NavBar />

      <div className="sticky top-16 z-20 bg-[var(--color-background)] border-b border-[var(--color-border)]">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-9 h-9 shrink-0 rounded-lg bg-[var(--color-primary-soft)] text-[var(--color-primary)] flex items-center justify-center">
              <UsersIcon className="w-5 h-5" />
            </span>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-semibold text-[var(--color-text-strong)] truncate">
                Association Requests
              </h1>
              <p className="text-xs text-[var(--color-text-muted)]">
                Self-registered seekers asking to join your bureau
              </p>
            </div>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] disabled:opacity-60 px-3 py-1.5 text-sm font-medium transition-colors"
          >
            <RefreshIcon className="w-4 h-4" />
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          {STATUSES.map((s) => (
            <button
              key={s.value}
              onClick={() => setStatusFilter(s.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === s.value
                  ? "bg-[var(--color-primary)] text-[var(--color-on-primary)]"
                  : "border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)]"
              }`}
            >
              {s.label}
              <span className="ml-1.5 text-xs opacity-80">
                ({counts[s.value] ?? 0})
              </span>
            </button>
          ))}
        </div>

        {error && (
          <div className="rounded-lg border border-[var(--color-danger-border)] bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger-text)]">
            {error}
          </div>
        )}

        {!loading && filtered.length === 0 && !error && (
          <div className="text-center py-12 text-sm text-[var(--color-text-muted)]">
            No requests in this view.
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((it) => {
            const seeker = it.publicUser || {};
            const busy = busyIds.has(it._id);
            const isPending = it.status === "pending";
            return (
              <div
                key={it._id}
                className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4 flex flex-col gap-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                      Request {formatDate(it.createdAt)}
                    </div>
                    <div className="text-sm font-semibold text-[var(--color-text-strong)] flex items-center gap-2">
                      <span className="truncate">
                        {seeker.fullName || "Unknown seeker"}
                      </span>
                      <GenderBadge gender={seeker.gender} />
                    </div>
                    <div className="text-xs text-[var(--color-text-muted)] truncate">
                      {seeker.email || "—"}
                      {seeker.contactNumber && (
                        <>
                          {" · "}
                          <a
                            href={`tel:${seeker.contactNumber}`}
                            className="hover:underline"
                          >
                            {seeker.contactNumber}
                          </a>
                        </>
                      )}
                    </div>
                    <div className="text-xs text-[var(--color-text-muted)]">
                      {seeker.age && <>Age {seeker.age}</>}
                      {seeker.city && <> · {seeker.city}</>}
                    </div>
                  </div>
                  <span
                    className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                      pillClass[it.status]
                    }`}
                  >
                    {it.status}
                  </span>
                </div>

                {it.message && (
                  <div className="text-xs">
                    <div className="font-semibold text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-0.5">
                      Message from seeker
                    </div>
                    <div className="whitespace-pre-wrap text-[var(--color-text)]">
                      {it.message}
                    </div>
                  </div>
                )}

                {!isPending && it.bureauResponse && (
                  <div className="text-xs">
                    <div className="font-semibold text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-0.5">
                      Your response
                    </div>
                    <div className="whitespace-pre-wrap text-[var(--color-text)]">
                      {it.bureauResponse}
                    </div>
                  </div>
                )}

                {isPending && (
                  <>
                    <textarea
                      rows={2}
                      value={responseDrafts[it._id] || ""}
                      onChange={(e) =>
                        setResponseDrafts((d) => ({
                          ...d,
                          [it._id]: e.target.value,
                        }))
                      }
                      placeholder="Optional response note"
                      className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-xs"
                    />
                    <div className="flex gap-2 flex-wrap">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => decide(it._id, "approve")}
                        className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] disabled:opacity-60 px-3 py-1.5 text-sm font-semibold transition-colors"
                      >
                        <SaveIcon className="w-4 h-4" />
                        Approve & onboard
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => decide(it._id, "reject")}
                        className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] disabled:opacity-60 px-3 py-1.5 text-sm font-medium transition-colors"
                      >
                        <CancelIcon className="w-4 h-4" />
                        Reject
                      </button>
                    </div>
                  </>
                )}

                {/* Client rating + review widget for active or ended
                    associations. Bureau can rate/review at any time;
                    "End relationship" closes the relationship and saves
                    the draft rating in one call. */}
                {(it.status === "approved" || it.status === "ended") && (
                  <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                        Rate this client (private)
                      </div>
                      {it.clientReviewUpdatedAt && (
                        <span className="text-[10px] text-[var(--color-text-muted)]">
                          Saved {formatDate(it.clientReviewUpdatedAt)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <StarRating
                        value={draftFor(it._id, it).rating}
                        onChange={(v) =>
                          setDraftField(it._id, "rating", v, it)
                        }
                      />
                      <span className="text-xs text-[var(--color-text-muted)]">
                        {draftFor(it._id, it).rating
                          ? `${draftFor(it._id, it).rating}/5`
                          : "Tap a star"}
                      </span>
                    </div>
                    <textarea
                      rows={2}
                      value={draftFor(it._id, it).comment}
                      onChange={(e) =>
                        setDraftField(it._id, "comment", e.target.value, it)
                      }
                      placeholder="Notes about working with this client (visible only to your bureau)"
                      className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-xs"
                    />
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => saveClientReview(it)}
                        className="inline-flex items-center gap-1 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] disabled:opacity-60 px-3 py-1.5 text-xs font-semibold transition-colors"
                      >
                        <SaveIcon className="w-3 h-3" />
                        Save review
                      </button>
                    </div>
                  </div>
                )}

                {/* End-relationship action — only on approved rows. The
                    bureau can attach a closing note (response textarea)
                    and the draft rating gets saved in the same call. */}
                {it.status === "approved" && (
                  <div className="space-y-2">
                    {endingId === it._id ? (
                      <>
                        <textarea
                          rows={2}
                          value={responseDrafts[it._id] || ""}
                          onChange={(e) =>
                            setResponseDrafts((d) => ({
                              ...d,
                              [it._id]: e.target.value,
                            }))
                          }
                          placeholder="Closing note for the client (optional)"
                          className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-xs"
                        />
                        <div className="flex gap-2 flex-wrap">
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => endAssociation(it)}
                            className="inline-flex items-center gap-1 rounded-lg border border-red-300 bg-red-50 hover:bg-red-100 text-red-700 disabled:opacity-60 px-3 py-1.5 text-xs font-semibold transition-colors"
                          >
                            <CancelIcon className="w-3 h-3" />
                            Confirm end
                          </button>
                          <button
                            type="button"
                            onClick={() => setEndingId(null)}
                            className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] px-3 py-1.5 text-xs font-medium transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setEndingId(it._id)}
                        className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-border)] hover:bg-red-50 hover:border-red-300 text-red-600 disabled:opacity-60 px-3 py-1.5 text-xs font-medium transition-colors"
                      >
                        <CancelIcon className="w-3 h-3" />
                        End relationship
                      </button>
                    )}
                  </div>
                )}

                {it.respondedAt && (
                  <div className="text-[10px] text-[var(--color-text-muted)] mt-auto">
                    {it.status === "ended"
                      ? `Ended ${formatDate(it.endedAt || it.respondedAt)}${
                          it.endedBy
                            ? ` (by ${it.endedBy === "bureau" ? "you" : "client"})`
                            : ""
                        }`
                      : `Decided ${formatDate(it.respondedAt)}`}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BureauAssociations;
